/**
 * Onboarding launchpad progress for the demo. The launchpad + sidebar locks derive from one mocked
 * call (GET /v2/members/tasks/), so we drive them by which tasks report `completed`. Module-scoped
 * (shared with the MSW handler); resets on reload.
 */
import { DEFAULT_DEMO_STATE } from './stateTax';
import { Usertype } from './types';

import UserType from 'modules/common/types/UserType.enum';
import {
    extractTaskNamesFromSchema,
    getSchemaFromUserType,
} from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema/constants';

// First load shows the first row (formation / business verification) already done.
const DEFAULT_ONBOARDING_STEP = 1;

// Ordered launchpad rows for a journey, each a group of task names; derived from the real schema so
// it can't drift. Step N done = every name in rows[0..N-1] completed.
// The schema is state-dependent (NY/KY/NE differ from the standard sequence), so the prospect's
// state is passed through rather than assumed to be the default.
export function getOnboardingStepTaskNames(journey: Usertype, state: string = DEFAULT_DEMO_STATE): string[][] {
    const schema = getSchemaFromUserType(journey as unknown as UserType, state);
    return schema.map((row) => extractTaskNamesFromSchema([row]));
}

function readInitialStep(): number {
    const raw = new URLSearchParams(window.location.search).get('onboarding_step');
    const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_ONBOARDING_STEP;
}

const currentStep = readInitialStep();

export function getOnboardingStep(): number {
    return currentStep;
}

// Individual tasks finished by walking a real flow. Tracked per-task (not per-row) so a multi-step
// flow (e.g. business verification) advances one step at a time instead of jumping to the end.
const manuallyCompletedTaskNames = new Set<string>();

export function completeOnboardingTask(taskName: string): void {
    manuallyCompletedTaskNames.add(taskName);
}

// Forget flow completions — on (re)entering a flow so it walks from the start.
export function resetManualCompletions(): void {
    manuallyCompletedTaskNames.clear();
}

// Names done via step (rows 0..step-1) plus any finished by walking a flow.
export function getCompletedOnboardingTaskNames(
    journey: Usertype,
    step: number,
    state: string = DEFAULT_DEMO_STATE
): Set<string> {
    const completed = new Set(getOnboardingStepTaskNames(journey, state).slice(0, step).flat());
    manuallyCompletedTaskNames.forEach((name) => completed.add(name));
    return completed;
}
